import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { nzTodayIso } from '@/lib/dates';
import { cutoffDate } from '@/lib/billing';
import api from '@/lib/api';
import { toast } from 'sonner';
import { JobBillingDetailWorkspace } from './JobBillingDetailWorkspace';
import { useMaterialReview } from './MaterialBillingAttention';

export default function JobCardBilling(props) {
  const {user} = useAuth();
  if (!['admin','office','super_admin'].includes(user?.role)) return null;
  return <BillingPanel {...props} />;
}
function BillingPanel({job, initialDate, defaultExpanded = false}) {
  const navigate = useNavigate();
  const review = useMaterialReview();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [month, setMonth] = useState((initialDate || nzTodayIso()).slice(0,7));
  const [marking, setMarking] = useState(null);
  const [revision, setRevision] = useState(0);
  const billingJob = useMemo(() => ({...job,job_id:job.id}), [job]);
  const [year, monthNumber] = month.split('-').map(Number);
  const sel = {year,month:monthNumber,label:new Date(year,monthNumber-1,1).toLocaleDateString('en-NZ',{month:'long',year:'numeric'})};
  const invoiceNow = () => review.check(job.id, token => navigate(`/app/jobs/${job.id}/charge-up`,{state:{materialReviewToken:token}}));
  const markInvoiced = () => review.check(job.id, async material_review_token => {
    setMarking(job.id);
    try {
      const {data:org} = await api.get('/org/me');
      const cut = cutoffDate(org?.invoice_cutoff_day);
      if (!window.confirm(`Mark all unclaimed hours on ${job.job_number || job.title} ${cut ? `up to ${cut}` : 'to date'} as invoiced outside Fieldlink? They'll be locked so they can't be billed again.`)) return;
      await api.post(`/jobs/${job.id}/hours/mark-invoiced`,{to_date:cut || undefined,material_review_token});
      toast.success('Hours marked as invoiced'); setRevision(v=>v+1);
    } catch(e) {
      if(e.response?.data?.detail?.code === 'material_review_required') throw e;
      toast.error('Could not mark hours as invoiced. Please retry.');
    } finally {setMarking(null);}
  });
  return <section className="rounded-xl border bg-background p-3 sm:p-4 my-4" aria-label="Job billing">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h2 className="font-semibold">Job billing</h2><p className="text-sm text-muted-foreground">Labour, travel, plant, materials, invoices and billing readiness.</p></div>
      <button type="button" className="min-h-11 px-3 text-sm font-semibold text-orange-700 dark:text-orange-300" aria-expanded={expanded} onClick={()=>setExpanded(v=>!v)}>{expanded ? 'Hide billing details' : 'Show billing details'}</button>
    </div>
    {expanded && <div className="mt-3 space-y-3">
      <label className="flex flex-wrap items-center gap-3 text-sm">Billing month<input aria-label="Billing month" type="month" min="2020-01" max="2050-12" className="min-h-11 rounded-md border bg-background px-3" value={month} onChange={e=>{if (/^\d{4}-\d{2}$/.test(e.target.value) && e.target.value >= '2020-01' && e.target.value <= '2050-12') setMonth(e.target.value);}} /></label>
      <JobBillingDetailWorkspace key={revision} embedded job={billingJob} sel={sel} onInvoiceNow={invoiceNow} onMarkInvoiced={markInvoiced} marking={marking} />
    </div>}
    {review.dialog}
  </section>;
}
